// Inbound Requests combo: one stacked bar per time slot (status segments + ping-shield event
// types) over a cyan area-fill Requests line drawn behind the stack.
// Mirrors calls_receivables_and_payables_combo.js.
import { Chart } from 'chart.js'

import { chartPalette, barDepthFill, barCapBorderRadius, verticalGradient, cssVar } from './palette'

// Presenter colors arrive as `var(--chart-*)`; Chart.js needs a resolved color for fills.
function resolveColor(value) {
    if (typeof value !== 'string') return value
    const match = value.match(/^var\((--[\w-]+)\)$/)
    return match ? cssVar(match[1], value) : value
}

export function buildInboundRequestsByStatus(
    canvas,
    optionsFromParam,
    { plugins = [] } = {},
) {
    const { datasets: sourceDatasets = [], labels = [] } = optionsFromParam.data || {}
    const palette = chartPalette()

    const requestsDataset = sourceDatasets[0] || {}
    const barStatusDatasets = sourceDatasets.slice(1)

    // Every bar shares one stack, so each time slot renders as a single composition column.
    // The explicit stack id also keeps the Requests line (stack undefined) out of
    // barCapBorderRadius's top-of-stack lookup.
    const barBase = {
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.92,
        categoryPercentage: 0.78,
        stack: 'inbound',
        order: 2,
    }

    const datasets = barStatusDatasets.map((dataset) => {
        const rawColor = dataset.borderColor || dataset.backgroundColor
        const color = resolveColor(rawColor)
        return {
            ...barBase,
            label: dataset.label,
            data: dataset.data,
            backgroundColor: barDepthFill(color),
            borderColor: color,
        }
    })

    datasets.push({
        type: 'line',
        label: requestsDataset.label,
        yAxisID: 'y',
        data: requestsDataset.data,
        borderColor: palette.callsLine,
        backgroundColor: verticalGradient([
            [0, palette.areaFillTop],
            [1, palette.areaFillBottom],
        ]),
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: palette.callsLine,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        borderJoinStyle: 'round',
        fill: true,
        // Higher order than the bars (2) draws the line first, i.e. behind the stack.
        order: 1,
    })

    return new Chart(canvas, {
        plugins: [...plugins],
        data: { labels, datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = Number(item.parsed.y).toLocaleString()
                            return `${item.dataset.label}: ${value}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: { drawTicks: false },
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 12,
                    },
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    ticks: { padding: 8 },
                },
            },
            elements: {
                bar: {
                    borderRadius: barCapBorderRadius(false, true),
                    borderSkipped: false,
                },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
